import React from 'react';
import axios from '../../../../axios-config.js';
import {Link} from 'react-router-dom';
import {connect} from 'react-redux';
import { notifyError, notifySuccess, notifyWarning } from '../../../../utils/notifications.js';

function getApiMessage(error, fallback) {
  return error?.response?.data?.message || error?.response?.data?.error?.message || fallback;
}

function prettyElapsedCalc(lastLoginTime) {
  if (lastLoginTime === null) {
    return 'Never';
  }

  let lastLogin = new Date(lastLoginTime);
  let currentTime = new Date();
  let unit = "day";

  let duration = Math.floor((currentTime - lastLogin) / 86400000);

  if (duration == 0) {
    unit = "hour";
    duration = Math.floor((currentTime - lastLogin) / 3600000)
    if (duration == 0) {
      unit = "minute"
      duration = Math.floor((currentTime - lastLogin) / 60000)
      if (duration == 0) {
        unit = "second"
        duration = Math.floor((currentTime - lastLogin) / 1000)
      }
    }

  }

  if (duration > 1) {
    unit = unit + 's';
  }

  return `${duration} ${unit} ago`

}

const CompanyField = (props) => {
  if (props.editing) {
    return (
      <div className="CompanyField modern">
        <div className="CompanyFieldLabelInline">{props.title}</div>
        <input className="CompanyEditorInput" onChange={props.onChange} value={props.value} placeholder={props.placeholder || props.title} />
        <div className="CompanyFieldSave" onClick={()=>{props.onSave();props.toggleEdit();}}>{props.submitText ? props.submitText : "Save"}</div>
      </div>
    )
  }
  return (
    <div className="CompanyField modern static">
      <div className="CompanyFieldLabelInline">{props.title}</div>
      <div className="CompanyFieldValue">{props.value || '-'}</div>
      <div><div onClick={props.toggleEdit} className="toggleEditButton modern">Edit</div></div>
    </div>
  )
}

const AddingUser = (props) => {
  return (
    <div className={"AddingUserField" + (props.visible ? " visible" : "")}>
      <div className="AddingUserCard">
        <div className="AddingUserCopy">
          <div className="AddingUserTitle">EXISTING USER EMAIL</div>
          <div className="AddingUserHint">Enter the user’s email address to add them to this company.</div>
        </div>
        <div className="AddingUserControls">
          <input onChange={props.onChange} value={props.value} placeholder={props.placeholder} />
          <div className="AddingUserSave" onClick={props.handleSubmit}>{props.submitText ? props.submitText : "Save"}</div>
        </div>
      </div>
    </div>
  )
}

const ResetPasswordConfirmDialog = (props) => {
  if (!props.email) {
    return null;
  }

  const confirmText = props.confirmText || '';
  const canSubmit = confirmText.toUpperCase() === 'YES' && !props.saving;

  return (
    <div className="CompanyEditorConfirmOverlay" role="presentation">
      <div className="CompanyEditorConfirmDialog" role="dialog" aria-modal="true" aria-labelledby="resetPasswordConfirmTitle">
        <div className="CompanyEditorConfirmEyebrow">Password reset</div>
        <div className="CompanyEditorConfirmTitle" id="resetPasswordConfirmTitle">Reset user password?</div>
        <div className="CompanyEditorConfirmBody">
          This will send a password reset email to <strong>{props.email}</strong>. The user will be able to set a new password from that email.
        </div>
        <label className="CompanyEditorConfirmLabel" htmlFor="resetPasswordConfirmInput">Type YES to confirm</label>
        <input
          id="resetPasswordConfirmInput"
          className="CompanyEditorConfirmInput"
          value={confirmText}
          onChange={props.onConfirmTextChange}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              props.onCancel();
            }
          }}
          autoFocus
          disabled={props.saving}
        />
        <div className="CompanyEditorConfirmActions">
          <button type="button" className="CompanyEditorSecondaryButton" onClick={props.onCancel} disabled={props.saving}>Cancel</button>
          <button type="button" className="CompanyEditorPrimaryButton danger" onClick={props.onConfirm} disabled={!canSubmit}>
            {props.saving ? 'Sending…' : 'Send reset email'}
          </button>
        </div>
      </div>
    </div>
  );
}

const ProjectTokenControls = (props) => {
  const tokensForProject = (props.tokens || []).filter((token) => token.project_id === props.projectID);

  if (tokensForProject.length === 0) {
    return <div className="ProjectTokenEmpty">No share tokens</div>;
  }

  return (
    <div className="ProjectTokenControls">
      {tokensForProject.map((token, index) => {
        const shareURL = `https://inventum3d.com/c/${token.company_slug}/${token.project_slug}/?st=${token.token}`;

        let detailedLog = ``;
        let logLength = 0;
        for (var i = token.activity.length - 1; i >= 0; i--) {
          detailedLog += `${token.activity[i].ip} ${prettyElapsedCalc(token.activity[i].access_time)} \n`;
          logLength++;
          if (logLength > 9) {
            break;
          }
        }

        const handleURLCopy = () => {
          navigator.clipboard.writeText(shareURL)
          .then(() => {
            notifySuccess('Share token URL copied.', { title: 'Copied URL' });
          })
          .catch(() => {
            notifyError(`Could not copy automatically. URL: ${shareURL}`, { title: 'Copy failed', displayTime: 10000 });
          })
        }

        const toggleTokenExpiration = () => {
          axios.post(`/project/${token.project_id}/share/${token.token}`, { tokenExpired: !token.expired })
          .then(() => {
            props.updateData();
          })
          .catch((error) => {
            console.log(error);
          })
        }

        return (
          <div className="ProjectTokenRow" key={index}>
            <div className="RemoveUserButton" onClick={toggleTokenExpiration}>{token.expired ? "Unexpire" : "Expire"} Token</div>
            <div className="RemoveUserButton resetPassword" onClick={handleURLCopy} title={shareURL}>Copy URL</div>
            <div className="lastSeenCounter" title={detailedLog}>Last Access: {prettyElapsedCalc(token.last_access)}</div>
          </div>
        )
      })}
    </div>
  );
};

const ProjectsList = (props) => {
  return (
    <div className="ProjectsList">
      <div className="SectionHeaderRow">
        <div className="CompanyEditorSectionTitle">Projects</div>
        <div className="CreateProjectButton usersActionButton" onClick={props.handleCreateProject}>Create Project</div>
      </div>
      <div className="ProjectsEntryContainer projectsFieldList">
        {props.projects.map((project,index) => (
          <div key={index} className="CompanyField modern static projectFieldRow">
            <div className="CompanyFieldLabelInline">Project</div>
            <div className="CompanyFieldValue projectFieldContent">
              <div className="ProjectFieldName">{project.project_name}</div>
              <ProjectTokenControls projectID={project.project_id} tokens={props.tokens} updateData={props.updateData} />
            </div>
            <div>
              <div title={"Click to edit"} onClick={()=>{props.handleEditProject(project.project_id)}} className="toggleEditButton modern">Edit</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}


class UsersList extends React.Component {
  getCompanyID() {
    return String(this.props.companyID || '').split('/')[0];
  }

  constructor(props) {
    super(props);
    this.state= {
      addingUser:false,
      newUserEmail:"",
      managedUsers: [],
      selectedProjectByUser: {},
      usersLoading: true,
      usersError: '',
      resetPasswordEmail: '',
      resetPasswordConfirmText: '',
      resetPasswordSaving: false,
    };
    this.usersListRef = React.createRef();
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.fetchUsersWithRoles = this.fetchUsersWithRoles.bind(this);
    this.handleRoleChange = this.handleRoleChange.bind(this);
    this.handleGrantProjectOverride = this.handleGrantProjectOverride.bind(this);
    this.handleRevokeProjectOverride = this.handleRevokeProjectOverride.bind(this);
    this.handleAddExistingToggle = this.handleAddExistingToggle.bind(this);
    this.handleResetPassword = this.handleResetPassword.bind(this);
    this.handleCancelResetPassword = this.handleCancelResetPassword.bind(this);
    this.handleConfirmResetPassword = this.handleConfirmResetPassword.bind(this);
  }

  componentDidMount() {
    this.fetchUsersWithRoles();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.users !== this.props.users) {
      this.fetchUsersWithRoles();
    }
  }

  fetchUsersWithRoles() {
    this.setState({ usersLoading: true, usersError: '' });

    axios.get(`/company/${this.getCompanyID()}/users`)
    .then((response) => {
      if (response.data && Array.isArray(response.data.users)) {
        const usersWithLastLogin = response.data.users.map((user) => {
          const legacyUser = (this.props.users || []).find((u) => u.email === user.email);
          return Object.assign({}, user, {
            last_login: user.last_login || (legacyUser ? legacyUser.last_login : null),
          });
        });
        this.setState({managedUsers: usersWithLastLogin, usersLoading: false});
      } else {
        this.setState({managedUsers: [], usersLoading: false, usersError: 'Could not load role data'});
      }
    })
    .catch((error) => {
      const apiMessage = error && error.response && error.response.data && error.response.data.message ? error.response.data.message : 'Failed to load users/roles';
      this.setState({ usersLoading: false, usersError: apiMessage });
    });
  }

  handleAddExistingToggle() {
    const addingUser = !this.state.addingUser;
    this.setState({addingUser}, () => {
      if (addingUser && this.usersListRef.current) {
        this.usersListRef.current.scrollTo({top: 0, behavior: 'smooth'});
      }
    });
  }

  handleSubmit() {
    let state = this.state;
    if (state.newUserEmail.length == 0) {
      notifyWarning(`Email can't be blank`, { title: 'Add user' });
      return;
    }

    axios.post(`/company/${this.getCompanyID()}/users/`,{email:state.newUserEmail})
    .then((response) => {
      console.log('Added User!');
      this.props.updateData();
      this.fetchUsersWithRoles();
      this.setState({addingUser:false,newUserEmail:""});
    }).catch((error) => {
      console.log(error.response);
      notifyError(getApiMessage(error, 'Failed to add user.'), { title: 'Add user failed' });
    })
  }

  handleRemove(email) {
    let deleteConfirm = prompt("Type YES to remove user:","");
    if (deleteConfirm.toUpperCase() === "YES") {
      axios.delete(`/company/${this.getCompanyID()}/users/`,{data:{email}})
      .then((response) => {
        this.props.updateData();
        this.fetchUsersWithRoles();
      }).catch((error) => {
        console.log(error.response);
        notifyError(getApiMessage(error, 'Failed to remove user.'), { title: 'Remove user failed' });
      })
    }
  }

  handleResetPassword(email) {
    this.setState({
      resetPasswordEmail: email,
      resetPasswordConfirmText: '',
      resetPasswordSaving: false,
    });
  }

  handleCancelResetPassword() {
    if (this.state.resetPasswordSaving) {
      return;
    }

    this.setState({
      resetPasswordEmail: '',
      resetPasswordConfirmText: '',
      resetPasswordSaving: false,
    });
  }

  handleConfirmResetPassword() {
    const email = this.state.resetPasswordEmail;
    if (!email || this.state.resetPasswordConfirmText.toUpperCase() !== 'YES' || this.state.resetPasswordSaving) {
      return;
    }

    this.setState({ resetPasswordSaving: true });
    axios.post(`/forgotpassword/`,{email})
    .then(() => {
      this.props.updateData();
      this.setState({
        resetPasswordEmail: '',
        resetPasswordConfirmText: '',
        resetPasswordSaving: false,
      });
      notifySuccess(`Password reset email sent to ${email}.`, { title: 'Reset email sent', displayTime: 6000 });
    }).catch((error) => {
      console.log(error.response || error);
      const apiMessage = getApiMessage(error, `Failed to send password reset email to ${email}.`);
      this.setState({
        resetPasswordSaving: false,
      });
      notifyError(apiMessage, { title: 'Password reset failed', displayTime: 7000 });
    })
  }

  handleRoleChange(userID, role) {
    if (!userID) {
      notifyWarning('User data is still loading. Try again in a moment.', { title: 'Update role' });
      return;
    }

    axios.post(`/company/${this.getCompanyID()}/users/role`, { userID, role })
    .then(() => {
      this.fetchUsersWithRoles();
      this.props.updateData();
      notifySuccess('User role updated.', { title: 'Permissions updated' });
    })
    .catch((error) => {
      console.log(error.response || error);
      notifyError(getApiMessage(error, 'Failed to update user role.'), { title: 'Update role failed' });
    });
  }

  handleGrantProjectOverride(userID) {
    const projectID = this.state.selectedProjectByUser[userID];
    if (!projectID) {
      notifyWarning('Select a project first.', { title: 'Project override' });
      return;
    }

    axios.post(`/company/${this.getCompanyID()}/users/project/grant`, { userID, projectID, canEdit: true })
    .then(() => {
      this.props.updateData();
      this.fetchUsersWithRoles();
      notifySuccess('Project edit override granted.', { title: 'Permissions updated' });
    })
    .catch((error) => {
      console.log(error.response || error);
      notifyError(getApiMessage(error, 'Failed to grant project override.'), { title: 'Project override failed' });
    });
  }

  handleRevokeProjectOverride(userID) {
    const projectID = this.state.selectedProjectByUser[userID];
    if (!projectID) {
      notifyWarning('Select a project first.', { title: 'Project override' });
      return;
    }

    axios.post(`/company/${this.getCompanyID()}/users/project/revoke`, { userID, projectID })
    .then(() => {
      this.props.updateData();
      this.fetchUsersWithRoles();
      notifySuccess('Project edit override revoked.', { title: 'Permissions updated' });
    })
    .catch((error) => {
      console.log(error.response || error);
      notifyError(getApiMessage(error, 'Failed to revoke project override.'), { title: 'Project override failed' });
    });
  }

  render() {
    return (
      <div className="ProjectsList permissionsSurface usersTokensSurface" ref={this.usersListRef}>
        <div className="SectionHeaderRow">
          <div className="CompanyEditorSectionTitle">Users</div>
          <div className="SectionHeaderActions">
            <div className="CreateProjectButton usersActionButton" onClick={this.handleAddExistingToggle}>Add Existing User</div>
            <div className="CreateProjectButton usersActionButton" onClick={this.props.handleCreateUser}>Create New User</div>
          </div>
        </div>

        <div className="usersTokensBody">
          <AddingUser handleSubmit={this.handleSubmit} value={this.state.newUserEmail} onChange={(e)=>{this.setState({newUserEmail:e.target.value})}} visible={this.state.addingUser} submitText={"Add User"} placeholder={"Email"} />

          {this.state.usersLoading ? <div className="PermissionLoadState">Loading user roles…</div> : null}
          {this.state.usersError ? <div className="PermissionLoadState error">{this.state.usersError}</div> : null}

          {this.state.managedUsers.map((user,index) => <User {...user} projects={this.props.projects} selectedProjectID={this.state.selectedProjectByUser[user.id] || ''} onSelectProject={(projectID) => { this.setState({ selectedProjectByUser: Object.assign({}, this.state.selectedProjectByUser, { [user.id]: projectID }) }) }} onRoleChange={this.handleRoleChange} onGrantProjectOverride={this.handleGrantProjectOverride} onRevokeProjectOverride={this.handleRevokeProjectOverride} key={index} handleRemove={this.handleRemove} handleResetPassword={this.handleResetPassword} /> )}
        </div>

        <ResetPasswordConfirmDialog
          email={this.state.resetPasswordEmail}
          confirmText={this.state.resetPasswordConfirmText}
          saving={this.state.resetPasswordSaving}
          onConfirmTextChange={(event) => { this.setState({resetPasswordConfirmText: event.target.value}); }}
          onCancel={this.handleCancelResetPassword}
          onConfirm={this.handleConfirmResetPassword}
        />
      </div>
    )

  }
}

const UserManagementTable = (props) => {
  return (
    <div style={{ overflowX: 'auto', width: '100%' }}>
      <table className="LightsEditorContainer permissionsTable" style={{ width: '100%' }}>
        <thead>
          <tr className="CSSEditorRow">
            <th>Email</th>
            <th>Name</th>
            <th>Role</th>
            <th>Can View</th>
            <th>Can Edit</th>
            <th>Project Override</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {(props.users || []).map((user) => (
            <tr className="CSSEditorRow" key={user.id}>
              <td>{user.email}</td>
              <td>{`${user.first_name || ''} ${user.last_name || ''}`.trim()}</td>
              <td>
                <select className="permissionsSelect" value={user.role || 'viewer'} disabled={!user.id} onChange={(e) => props.onRoleChange(user.id, e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="editor">Editor</option>
                  <option value="viewer">Reviewer</option>
                </select>
              </td>
              <td><span className="PermissionScopeChip">All company projects</span></td>
              <td>
                {(user.role === 'admin' || user.role === 'editor') ? (
                  <span className="PermissionScopeChip full">All company projects</span>
                ) : ((user.projectOverrides || []).length > 0 ? (
                  <span className="PermissionScopeChip partial">{(user.projectOverrides || []).map((p) => p.project_name).join(', ')}</span>
                ) : (
                  <span className="PermissionScopeChip none">None</span>
                ))}
              </td>
              <td>
                {user.role === 'viewer' ? (
                  <select className="permissionsSelect" value={props.selectedProjectByUser[user.id] || ''} onChange={(e) => props.onSelectProject(user.id, e.target.value)}>
                    <option value="">Select project…</option>
                    {(props.projects || []).map((project) => (
                      <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="PermissionScopeChip full">Role grants all projects</span>
                )}
              </td>
              <td>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {user.role === 'viewer' ? (
                    <>
                      <div className="RemoveUserButton PermissionActionButton grant" onClick={() => props.onGrantProjectOverride(user.id)}>Grant Edit</div>
                      <div className="RemoveUserButton PermissionActionButton revoke" onClick={() => props.onRevokeProjectOverride(user.id)}>Revoke Edit</div>
                    </>
                  ) : null}
                  <div className="RemoveUserButton PermissionActionButton reset" onClick={() => props.handleResetPassword(user.email)}>Reset</div>
                  <div className="RemoveUserButton PermissionActionButton remove" onClick={() => props.handleRemove(user.email)}>Remove</div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const User = (props) => {
  let lastLogin = prettyElapsedCalc(props.last_login);
  const fullName = `${props.first_name || ''} ${props.last_name || ''}`.trim();
  const canEditAll = props.role === 'admin' || props.role === 'editor';
  const projectOverrides = props.projectOverrides || [];

  return (
    <div className="UserPermissionCard" title={`Email:${props.email}`}>
      <div className="UserPermissionCardHeader">
        <div>
          <div className="UserPermissionName">{fullName || props.email}</div>
          <div className="UserPermissionEmail">{props.email}</div>
        </div>
        <div className="lastSeenCounter">Last Login: {lastLogin}</div>
      </div>

      <div className="UserPermissionAccessGrid">
        <div>
          <div className="PermissionLabel">Can view</div>
          <div className="PermissionValue">All company projects</div>
        </div>
        <div>
          <div className="PermissionLabel">Can edit</div>
          <div className="PermissionValue">
            {canEditAll ? 'All company projects' : (projectOverrides.length > 0 ? projectOverrides.map((p) => p.project_name).join(', ') : 'None')}
          </div>
        </div>
      </div>

      <div className="UserPermissionControlsRow">
        <select className="permissionsSelect" value={props.role || 'viewer'} disabled={!props.id} onChange={(e) => props.onRoleChange(props.id, e.target.value)}>
          <option value="admin">Admin</option>
          <option value="editor">Editor</option>
          <option value="viewer">Reviewer</option>
        </select>

        {props.role === 'viewer' ? (
          <select className="permissionsSelect" value={props.selectedProjectID || ''} onChange={(e) => props.onSelectProject(e.target.value)}>
            <option value="">Select project…</option>
            {(props.projects || []).map((project) => (
              <option key={project.project_id} value={project.project_id}>{project.project_name}</option>
            ))}
          </select>
        ) : (
          <div className="PermissionScopeChip full">Role grants all projects</div>
        )}

        {props.role === 'viewer' ? <div className="RemoveUserButton PermissionActionButton grant" onClick={() => props.onGrantProjectOverride(props.id)}>Grant Edit</div> : null}
        {props.role === 'viewer' ? <div className="RemoveUserButton PermissionActionButton revoke" onClick={() => props.onRevokeProjectOverride(props.id)}>Revoke Edit</div> : null}
        <div className="RemoveUserButton PermissionActionButton reset" onClick={() => {props.handleResetPassword(props.email)}}>Reset</div>
        <div className="RemoveUserButton PermissionActionButton remove" onClick={() => {props.handleRemove(props.email)}}>Remove</div>
      </div>
    </div>
  )
}

const Submit = (props) => {
  return (
    <div onClick={props.onClick} className="CreateCompanySubmit">Company Editing is not currently supported</div>
  )
}


class CompanyEditor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      name:props.company.name,
      website:props.company.website || '',
      editingName:false,
      editingWebsite:false,
      editingSlug:false,
      id:props.company.id,
      slug:props.company.slug,
      deleting:false,
    };
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
  }

  handleSubmit() {
    console.log('Updating');
    let state = this.state;
    if (state.name.length === 0) {
      notifyWarning(`Name can't be blank`, { title: 'Company details' });
      return;
    }

    if (state.slug.length === 0) {
      notifyWarning(`Slug can't be blank`, { title: 'Company details' });
      return;
    }

    axios.post(`/company/${String(state.id || '').split('/')[0]}`, {name:state.name, website:state.website, slug:state.slug})
    .then((response) => {
      console.log(response);
      notifySuccess('Company details updated.', { title: 'Company details' });
    }).catch((error) => {
      console.log(error.response || error);
      notifyError(getApiMessage(error, 'Failed to update company details.'), { title: 'Company update failed' });
    })
  }

  handleDelete() {
    if (!this.props.isSuperuser) {
      return;
    }

    const companyID = String(this.state.id || '').split('/')[0];
    const expectedName = String(this.state.name || '').trim();

    const firstConfirm = window.confirm(`Delete company "${expectedName}" and all related projects? This cannot be undone.`);
    if (!firstConfirm) {
      return;
    }

    const typed = window.prompt(`Type the company name exactly to confirm deletion:\n\n${expectedName}`);
    if (typed !== expectedName) {
      notifyWarning('Company name did not match. Deletion cancelled.', { title: 'Delete company' });
      return;
    }

    this.setState({ deleting:true });
    axios.delete(`/company/${companyID}`, { data: { confirm: 'DELETE' } })
      .then(() => {
        this.props.updateData();
        this.props.handleCloseEditor();
      })
      .catch((error) => {
        notifyError(getApiMessage(error, 'Failed to delete company.'), { title: 'Delete company failed' });
        this.setState({ deleting:false });
      });
  }

//props.company.website

  render() {
    let company = this.props.company;
    return (
      <div className="CompanyEditorModal">
        <div className="CompanyEditorHeader">
          <div className="CompanyEditorTitle">Company Settings</div>
          <div className="CompanyEditorSubtitle">Manage company metadata, projects, users, roles and project overrides.</div>
        </div>

        <div className="CompanyEditorSection">
          <div className="CompanyEditorSectionTitle">Details</div>
          <div className="CompanyEditorTwoColumnRow companyDetailsNameSlugRow">
            <CompanyField title={"Name"} editing={this.state.editingName} value={this.state.name} onSave={this.handleSubmit} toggleEdit={()=>{this.setState({editingName:!this.state.editingName})}} onChange={(e)=>{this.setState({name:e.target.value})}} />
            <CompanyField title={"Slug"} editing={this.state.editingSlug} value={this.state.slug} onSave={this.handleSubmit} toggleEdit={()=>{this.setState({editingSlug:!this.state.editingSlug})}} onChange={(e)=>{this.setState({slug:e.target.value})}} />
          </div>
          <CompanyField title={"Website"} editing={this.state.editingWebsite} value={this.state.website} onSave={this.handleSubmit} toggleEdit={()=>{this.setState({editingWebsite:!this.state.editingWebsite})}} onChange={(e)=>{this.setState({website:e.target.value})}} />
        </div>

        <div className="CompanyEditorSection">
          <ProjectsList handleEditProject={this.props.handleEditProject} handleCreateProject={this.props.handleCreateProject} projects={company.projects} tokens={company.tokens} updateData={this.props.updateData} />
        </div>

        <div className="CompanyEditorSection usersTokensSection">
          <UsersList updateData={this.props.updateData} companyID={this.state.id} handleCreateUser={this.props.handleCreateUser} users={company.users} projects={company.projects} tokens={company.tokens} />
        </div>

        {this.props.isSuperuser ? (
          <div className="CompanyEditorSection">
            <div className="CompanyEditorSectionTitle">Danger Zone</div>
            <button type="button" className="RemoveUserButton PermissionActionButton remove DangerZoneButton" onClick={this.handleDelete}>
              {this.state.deleting ? 'Deleting…' : 'Delete Company (Superuser)'}
            </button>
          </div>
        ) : null}
      </div>
    )
  }
}

export default CompanyEditor
